import { createApi } from "@reduxjs/toolkit/query/react";
import { axiosBaseQuery } from "@src/service";
import { DroneEntity } from "@src/core/features/drone/entities";
import { IGetDronesRequest, IGetDronesResponse, IGetFleetDronesRequest, IGetPaginatedDronesRequest, IGetPaginatedDronesResponse } from "./types";

// Single-request size with headroom over the ~150 prod fleet cap, so the whole fleet comes back
// in one call (no pagination) — including on every UPDATE_DRONES refetch. The follow-up below is
// only a safety net if the fleet ever outgrows this.
const DRONES_FETCH_SIZE = 200;

export const baseDroneApi = createApi({
  baseQuery: axiosBaseQuery(),
  reducerPath: 'baseDroneApi',
  tagTypes: ['GetDrones', 'GetDroneHistory', 'GetDroneMaintenance', 'Drone', 'DroneFirmware'],
  endpoints: (builder) => ({
    getDrones: builder.query<IGetDronesResponse, IGetDronesRequest | void>({
      queryFn: async (arg, _api, _extraOptions, baseQuery) => {
        const statusParam = arg && arg.status ? { status: arg.status } : {};
        const fetchDrones = (size: number) =>
          baseQuery({
            url: '/api/v1/drone',
            method: 'GET',
            params: { page: 0, size, ...statusParam },
          });

        // One shot for the whole fleet (expected: total ≤ prod cap < DRONES_FETCH_SIZE).
        const firstPage = await fetchDrones(DRONES_FETCH_SIZE);
        if (firstPage.error) return { error: firstPage.error };

        const firstData = firstPage.data as IGetDronesResponse;
        const totalCount = firstData.totalCount ?? firstData.data.length;

        // Everything already returned → done in a single request.
        if (firstData.data.length >= totalCount) return { data: firstData };

        // Safety net: fleet outgrew DRONES_FETCH_SIZE — refetch sized to the actual total.
        const fullPage = await fetchDrones(totalCount);
        if (fullPage.error) return { error: fullPage.error };

        return { data: fullPage.data as IGetDronesResponse };
      },
      providesTags: ['GetDrones'],
    }),
    getFleetDrones: builder.query<IGetDronesResponse, IGetFleetDronesRequest | void>({
      query: (arg) => ({
        url: '/api/v1/fleet/drones',
        method: 'GET',
        params: {
          scope: (arg && arg.scope) || 'SELECTED_HUB',
          ...(arg && arg.hubId != null ? { hubId: arg.hubId } : {}),
        },
      }),
      transformResponse: (resp: { data?: DroneEntity[]; total?: number; totalCount?: number; timestamp?: number }) => ({
        data: resp.data ?? [],
        totalCount: resp.totalCount ?? resp.total ?? resp.data?.length ?? 0,
        timestamp: resp.timestamp ?? Date.now(),
      }),
      providesTags: ['GetDrones'],
    }),
    getPaginatedDrones: builder.query<IGetPaginatedDronesResponse, IGetPaginatedDronesRequest>({
      query: ({ page, size, sort }) => ({
        url: '/api/v1/drone',
        method: 'GET',
        params: {
          page,
          size,
          sort,
        },
      }),
      providesTags: ['GetDrones'],
    }),
  })
});

export const { useGetDronesQuery, useGetFleetDronesQuery, useGetPaginatedDronesQuery } = baseDroneApi;